import React, { useState } from "react";
import { ComponentConfig, DropZone, usePuck } from "@measured/puck";
import Wrapper from "../../../utils/Wrapper";
import { BsTrophyFill } from "react-icons/bs";
import { Tooltip } from "../../../utils/Tooltip";
import { EditableText } from "../../../utils/components";
import { useWebsiteContext } from "../../../../context/website";
import { ImageField } from "../../../puck_fields";
import { type globalCommonFieldsType, createDefaultProps, globalCommonFields, type textFieldsType, textFields } from "../../../utils/CommonFields";
import { generateSpacingCSS, generateBorderCSS, GetCurrentFont } from '../../../helpers'
import { themes } from "../../../../theme";

export type HeroSectionProps = globalCommonFieldsType & {
    title: {
        text: string;
        typography: textFieldsType
    };
    description: {
        text: string;
        typography: textFieldsType
    };
    align?: string;
    image?: {
        mode?: "inline" | "background";
        url?: string;
    };
};

export const HeroSection: ComponentConfig<HeroSectionProps> = {
    fields: {
        title: {
            type: "object",
            objectFields: {
                text: { type: "textarea", label: "Text" },
                typography: {
                    type: "object",
                    objectFields: textFields,
                    label: "Typography"
                },
            },
            label: "Title"
        },
        description: {
            type: "object",
            objectFields: {
                text: { type: "textarea", label: "Text" },
                typography: {
                    type: "object",
                    objectFields: textFields,
                    label: "Typography"
                },
            },
            label: "Description"
        },
        align: {
            type: "radio",
            options: [
                { label: "left", value: "left" },
                { label: "center", value: "center" },
            ],
            label: "Align"
        },
        image: {
            type: "object",
            objectFields: {
                url: { ...ImageField, label: "Url" },
                mode: {
                    type: "radio",
                    options: [
                        { label: "inline", value: "inline" },
                        { label: "background", value: "background" },
                    ],
                    label: "Mode"
                },
            },
            label: "Image"
        },
        ...globalCommonFields
    },
    defaultProps: {
        title: {
            text: "Revitalize Your Essence",
            typography: createDefaultProps("text", {
                font: { name: "Roboto" },
                fontSize: 35,
                fontWeight: 600,
                lineHeight: 1.3,
            }),
        },
        description: {
            text: "Discover a sanctuary of beauty and relaxation. Experience personalized care and luxurious treatments designed to rejuvenate your mind, body, and spirit.",
            typography: createDefaultProps("text", {
                fontSize: 18,
                fontWeight: 400,
                lineHeight: 1.2,
                font: { name: "Montserrat" }
            }),
        },
        align: "left",
        image: {
            url: "https://www.wisewater.ca/wp-content/uploads/salon-spa.jpg",
            mode: "inline"
        },
        ...createDefaultProps("globalCommon", { spacing: { padding: { left: 0, top: 64, right: 0, bottom: 64, } } })
    },

    render: (data) => {
        const { websiteTheme } = useWebsiteContext();

        const handleChange = (e, type) => {
            const updatedData = { ...data };
            if (type === 'title') {
                updatedData.title.text = e.target.textContent;
            } else if (type === 'description') {
                updatedData.description.text = e.target.textContent;
            }

            return { updatedData: updatedData, type: "HeroSection_theme_6_variant_1" }
        };

        const titleData = {
            color: "#000000",
            size: 60,
            weight: 900,
            leading: 1.05,
        };

        const descriptionData = {
            color: "#000000",
            size: 20,
            weight: 600,
            leading: 1.3,
        };

        const theme = websiteTheme.name === "theme_6" ? websiteTheme.content : themes.theme_6
        const backgroundColor = data.backgroundColor.hex === "Default" ? theme.colors.background : data.backgroundColor.hex

        return (
            <Wrapper className="tw-relative" styles={{ backgroundColor: backgroundColor, ...generateBorderCSS(data.border), color: theme.colors.text }}>
                <div style={{ padding: generateSpacingCSS(data.spacing.padding), margin: generateSpacingCSS(data.spacing.margin) }} className={`${data.align === "center" ? "" : "lg:tw-grid-cols-2"} tw-grid tw-grid-cols-1 tw-items-center tw-gap-10 lg:tw-gap-3`}>
                    <div className={`tw-flex tw-flex-col tw-z-10 tw-gap-4 tw-text-center ${data.align === "center" ? "" : "lg:tw-text-left"} ${data.image?.mode === "background" ? "" : "md:tw-order-2 lg:tw-order-1"}`}>
                        <DropZone zone="hero_theme_6_variant_1_1" allow={["Text", "Image", "Link", "Video"]} />

                        <EditableText classes="tw-font-medium tw-text-center md:tw-text-left" handleChange={(e) => handleChange(e, "title")} data={data.title.typography}>
                            {data.title.text}
                        </EditableText>
                        <EditableText classes="" handleChange={(e) => handleChange(e, "description")} data={data.description.typography}>
                            {data.description.text}
                        </EditableText>

                        <DropZone zone="hero_theme_6_variant_1_2" allow={["Text", "Image", "Link", "Video"]} />
                    </div>

                    {data.image?.mode === "background" && (
                        <div className="tw-h-[220px] sm:tw-h-[300px] md:tw-h-[330px] lg:tw-h-[356px] xl:tw-h-[420px]">
                            <div
                                style={{
                                    backgroundImage: `url("${data.image?.url}")`,
                                    backgroundRepeat: "no-repeat",
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    position: "absolute",
                                    right: 0,
                                    top: 0,
                                    bottom: 0,
                                    left: 0
                                }}
                            ></div>

                            <div style={{
                                backgroundImage: `linear-gradient(-90deg, rgba(249, 250, 255, 0.6) 0%, rgba(249, 250, 255, 0.1) 80%)`,
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                bottom: 0,
                                left: 0,
                            }}></div>
                        </div>
                    )}

                    {data.align !== "center" && data.image?.mode !== "background" && data.image?.url && (
                        <div className="tw-w-full tw-h-full tw-hidden lg:tw-block">
                            <img
                                src={data.image?.url}
                                alt=""
                                className="tw-w-full tw-h-[530px] tw-object-cover"
                            />
                        </div>
                    )}

                </div>
            </Wrapper>
        );
    },
};

export const HeroSectionWrapper: React.FC<{
    children: React.ReactNode,
    showText: Boolean,
    showIcon: Boolean
}> = ({ children, showText, showIcon }) => {
    return <div className={`${showText ? "tw-px-5 tw-py-2 tw-w-full" : "tw-px-2 tw-py-1 tw-w-fit"} tw-border-transparent tw-rounded tw-shadow-md tw-flex tw-items-center tw-gap-4 tw-bg-background dark:tw-bg-backgroundDark tw-transition-all tw-duration-300 tw-ease-in-out`}>
        {showIcon &&
            (
                showText ?
                    <span>
                        <BsTrophyFill size={22} />
                    </span>
                    :
                    <Tooltip title="HeroSection">
                        <span>
                            <BsTrophyFill size={18} />
                        </span>
                    </Tooltip>
            )
        }
        {showText &&
            <div className="text-sm">HeroSection</div>
        }
    </div>;
};